import {
  ref,
  computed,
  watch,
  unref,
  nextTick,
  onBeforeUnmount,
  getCurrentInstance
} from "vue";
import store from "@/store";
import router from "@/router";
import hash from "object-hash";
import { DEFAULT_LIST_SCOPE } from "@/store/modules/data";
import type { DataModules } from "@/store/modules/data/modules";
import type {
  FilterDef,
  FilterOption,
  ActiveFilter,
  ScopingParam,
  SorterDef,
  SearchConfig,
  Persist,
  ListingState
} from "./types";
import { defaultToParam } from "./helpers";
import {
  serialiseConfig as serialiseListingConfig,
  defaultConfigString as defaultListingConfigString,
  deserialiseConfig as deserialiseListingConfig,
  SEPARATORS
} from "./serialise";
import type { Ref, ComputedRef } from "vue";

// ─── Registered IDs (dev-only collision warning) ────────────────────────────
// Ref-counted so HMR (new setup runs before old unmount) doesn't warn spuriously.

const registeredIds = new Map<string, number>();

// ─── Options ────────────────────────────────────────────────────────────────

export interface UseListingOptions {
  /** Unique ID — used as the URL query key */
  id: string;
  /** Vuex data module (e.g. `DataModules.ORDERS`) */
  module: DataModules;
  /** Store action override. Default: `"list"` */
  action?: string | Ref<string>;
  /** Store scope override */
  scope?: string;

  /**
   * `with` relations to eager-load.
   * Passed as `with` query param (comma-separated).
   */
  with?: Ref<string[]> | ComputedRef<string[]> | string[];
  /**
   * `with_count` relations.
   * Passed as `with_count` query param (comma-separated).
   */
  withCount?: string[];

  /**
   * Scoping params — non-interactive filters merged into every request.
   * Use for route-level constraints (e.g. `client_id`, tab filters).
   * Set `locked: true` on a param to surface it as a locked UI pill.
   *
   * Replaces the legacy `where` + `constFilters` options.
   */
  scopingParams?:
    | Ref<ScopingParam[]>
    | ComputedRef<ScopingParam[]>
    | ScopingParam[];

  /** Search field config. Omit to disable search. */
  search?: SearchConfig;

  /** Available filter definitions. Can be reactive. */
  filters?: Ref<FilterDef[]> | ComputedRef<FilterDef[]> | FilterDef[];
  /** Available sorter definitions */
  sorters?: Ref<SorterDef[]> | SorterDef[];

  /**
   * Default sort string. E.g. `"-created_at"` (descending) or `"name"` (ascending).
   * Default: `"-created_at"`
   */
  sort?: string;
  /** Default items per page. Default: `10` */
  limit?: number;

  /** How state is persisted. Default: `"url"` */
  persist?: Persist;
  /** For localStorage: include page in stored config. Default: false */
  saveWithPage?: boolean;
}

// ─── The Composable ─────────────────────────────────────────────────────────

export function useListing(options: UseListingOptions): ListingState {
  const SEP = SEPARATORS.config;
  const persistMode = options.persist ?? "url";
  const defaultSort = options.sort ?? "-created_at";
  const defaultLimit = options.limit ?? 10;
  const scope = options.scope ?? DEFAULT_LIST_SCOPE;
  const vm = getCurrentInstance()?.proxy;

  // ── Reactive State ──────────────────────────────────────────────────────

  const isLoading = ref(true);
  const isFetching = ref(false);
  const hasError = ref(false);

  const search = ref("");
  const activeFilters = ref<ActiveFilter[]>([]);
  const activeSort = ref<string>(defaultSort);
  const page = ref(1);
  const limit = ref(defaultLimit);

  let searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  let lastSyncedConfig = "";
  let routeWatcherStop: (() => void) | null = null;
  let initialised = false;
  let destroyed = false;
  let _afCounter = 0;
  const nextId = (): string => `af_${++_afCounter}`;

  if (process.env.NODE_ENV !== "production") {
    const count = (registeredIds.get(options.id) ?? 0) + 1;
    registeredIds.set(options.id, count);
    // Warn only when two instances are *concurrently* mounted. HMR re-mounts
    // decrement back to 1 when the old instance tears down, so this stays quiet
    // across hot reloads.
    if (count > 1) {
      // eslint-disable-next-line no-console
      console.warn(
        `[useListing] duplicate listing id "${options.id}" — two instances ` +
          `will stomp each other's URL state. Use distinct ids per listing.`
      );
    }
  }

  // ── Computed ────────────────────────────────────────────────────────────

  const filterDefs = computed<FilterDef[]>(() => {
    const defs = unref(options.filters ?? []) as FilterDef[];
    return defs.filter((d: FilterDef) => !d.if || d.if());
  });

  const sorterDefs = computed<SorterDef[]>(() => unref(options.sorters ?? []));

  const scopingParams = computed<ScopingParam[]>(
    () => unref(options.scopingParams ?? []) as ScopingParam[]
  );

  const lockedFilters = computed<ActiveFilter[]>(() =>
    scopingParams.value
      .filter(p => p.locked)
      .map(p => ({
        id: `locked:${p.field}`,
        defId: p.field,
        field: p.field,
        operator: p.operator || "eq",
        value: p.value,
        label: p.label,
        locked: true
      }))
  );

  // ── Store Getters ───────────────────────────────────────────────────────

  const data = computed<any[]>(() =>
    store.getters["data/listArray"]({
      storeModule: options.module,
      scope
    })
  );

  const total = computed<number | null>(() =>
    store.getters["data/total"]({
      storeModule: options.module,
      scope
    })
  );

  // ── Build API Params ──────────────────────────────────────────────────

  function buildApiParams(): Record<string, any> {
    const params: Record<string, any> = {};

    // Pagination
    params.limit = limit.value;
    params.offset = (page.value - 1) * limit.value;

    // Sort
    if (activeSort.value) {
      params.order = activeSort.value;
    }

    // `with` relations
    const withRels = unref(options.with) as string[] | undefined;
    if (withRels?.length) {
      params.with = withRels.join(",");
    }

    // `with_count` relations
    if (options.withCount?.length) {
      params.with_count = options.withCount.join(",");
    }

    // Search
    if (options.search && search.value) {
      const searchCfg = options.search;
      const op = searchCfg.operator || "like";
      const suffix = op !== "eq" ? `|${op}` : "";
      const val = op === "like" ? `%${search.value}%` : search.value;
      params[`filter[${searchCfg.field}${suffix}]`] = val;
    }

    // Scoping params (non-interactive constraints — `where` + `constFilters`)
    for (const sp of scopingParams.value) {
      const value = sp.value;
      if (value === undefined || value === null || value === "") continue;

      // `filter[...]`-prefixed shortcut: pass through verbatim
      if (sp.field.startsWith("filter[")) {
        params[sp.field] = value;
        continue;
      }

      const op = sp.operator || "eq";
      const def = filterDefs.value.find(d => d.field === sp.field);
      if (def?.toParam) {
        Object.assign(params, def.toParam(value, op));
      } else {
        Object.assign(params, defaultToParam(sp.field, op)(value, op));
      }
      if (def?.requires) Object.assign(params, def.requires);
    }

    // Active user filters
    for (const af of activeFilters.value) {
      // Try defId first, then fall back to field-based lookup
      // (defId may be stale if filter was restored from URL before defs were registered)
      const def =
        filterDefs.value.find(d => d.id === af.defId) ||
        filterDefs.value.find(d => d.field === af.field);
      if (def?.toParam) {
        Object.assign(params, def.toParam(af.value, af.operator));
      } else {
        Object.assign(
          params,
          defaultToParam(af.field, af.operator)(af.value, af.operator)
        );
      }
      if (def?.requires) Object.assign(params, def.requires);
    }

    return params;
  }

  // ── Fetch ─────────────────────────────────────────────────────────────

  let abortController: AbortController | null = null;

  async function executeFetch(
    opts: { ignoreStored?: boolean } = {}
  ): Promise<void> {
    const action = unref(options.action) ?? "list";
    const apiParams = buildApiParams();

    // Remove empty values
    for (const key of Object.keys(apiParams)) {
      if (apiParams[key] === "" || apiParams[key] === undefined) {
        delete apiParams[key];
      }
    }

    // Abort any in-flight request before issuing a new one
    abortController?.abort();
    abortController = new AbortController();
    const signal = abortController.signal;

    isFetching.value = true;

    try {
      await store.dispatch(`data/${options.module}/${action}`, {
        scope,
        vm,
        ignoreStored: opts.ignoreStored,
        params: apiParams,
        signal
      });
      hasError.value = false;
    } catch (error) {
      store.dispatch("api/handleError", error);
      hasError.value = true;
    } finally {
      // Only clear loading state if this fetch wasn't superseded by another.
      // (A newer fetch will have replaced abortController and aborted us.)
      if (!signal.aborted) {
        isLoading.value = false;
        isFetching.value = false;
      }
    }

    // Auto-redirect to page 1 if current page has no results
    if (
      !signal.aborted &&
      page.value > 1 &&
      data.value.length === 0 &&
      !hasError.value
    ) {
      page.value = 1;
      syncToUrl();
      return executeFetch(opts);
    }
  }

  // ── Public Actions ────────────────────────────────────────────────────

  async function fetch(): Promise<void> {
    syncToUrl();
    return executeFetch();
  }

  async function reload(): Promise<void> {
    cancel();
    syncToUrl();
    return executeFetch({ ignoreStored: true });
  }

  function cancel() {
    abortController?.abort();
    abortController = null;
    isFetching.value = false;
  }

  // ── Filter Actions ────────────────────────────────────────────────────

  async function setFilters(filters: ActiveFilter[]): Promise<void> {
    cancel();
    activeFilters.value = filters.map(f => ({
      ...f,
      id: f.id || nextId()
    }));
    page.value = 1;
    return fetch();
  }

  async function addFilter(filter: Omit<ActiveFilter, "id">): Promise<void> {
    cancel();
    activeFilters.value = [...activeFilters.value, { ...filter, id: nextId() }];
    page.value = 1;
    return fetch();
  }

  async function updateFilter(
    id: string,
    patch: Partial<Omit<ActiveFilter, "id">>
  ): Promise<void> {
    cancel();
    activeFilters.value = activeFilters.value.map(f =>
      f.id === id ? { ...f, ...patch } : f
    );
    page.value = 1;
    return fetch();
  }

  async function removeFilter(id: string): Promise<void> {
    cancel();
    activeFilters.value = activeFilters.value.filter(f => f.id !== id);
    page.value = 1;
    return fetch();
  }

  async function clearFilters(): Promise<void> {
    cancel();
    activeFilters.value = [];
    page.value = 1;
    return fetch();
  }

  // ── Sort Action ───────────────────────────────────────────────────────

  async function setSort(sort: string): Promise<void> {
    cancel();
    activeSort.value = sort;
    return fetch();
  }

  // ── Pagination Actions ────────────────────────────────────────────────

  async function setPage(p: number): Promise<void> {
    page.value = Math.max(1, p);
    return fetch();
  }

  async function setLimit(l: number): Promise<void> {
    limit.value = Math.max(1, l);
    page.value = 1;
    return fetch();
  }

  // ── Search Debounce ───────────────────────────────────────────────────

  if (options.search) {
    const debounceMs = options.search.debounce ?? 250;
    watch(search, (newVal, oldVal) => {
      if (newVal === oldVal) return;
      if (searchDebounceTimer) clearTimeout(searchDebounceTimer);
      searchDebounceTimer = setTimeout(() => {
        cancel();
        page.value = 1;
        fetch();
      }, debounceMs);
    });
  }

  // ── URL / Storage Persistence ─────────────────────────────────────────

  function serialiseConfig(): string {
    return serialiseListingConfig({
      page: page.value,
      limit: limit.value,
      sort: activeSort.value,
      filters: activeFilters.value,
      lockedFilters: lockedFilters.value
    });
  }

  function defaultConfigString(): string {
    return defaultListingConfigString({ defaultLimit, defaultSort });
  }

  function deserialiseConfig(raw: string) {
    const parsed = deserialiseListingConfig(raw, filterDefs.value);
    if (!parsed) return;

    page.value = parsed.page;
    limit.value = parsed.limit;
    activeSort.value = parsed.sort;

    activeFilters.value = parsed.entries
      .filter(e => !e.locked)
      .map(e => ({
        id: nextId(),
        defId: e.defId || `raw:${e.field}`,
        field: e.field,
        operator: e.operator,
        value: e.value,
        locked: false
      }));
    // const filters from URL are handled by the route config, not here
  }

  function syncToUrl() {
    if (persistMode === false) return;

    const config = serialiseConfig();
    lastSyncedConfig = config;

    if (persistMode === "localStorage") {
      const stored = options.saveWithPage
        ? config
        : config.substring(config.indexOf(SEP) + 1);
      localStorage.setItem(`config/${options.id}`, stored);

      // Update URL page param
      const query = { ...router.currentRoute.query };
      if (page.value === 1 || options.saveWithPage) {
        delete query["p"];
      } else {
        query["p"] = String(page.value);
      }
      if (hash(query) !== hash(router.currentRoute.query)) {
        router.replace({ query }).catch(() => {});
      }
      return;
    }

    // URL mode
    const query = { ...router.currentRoute.query };
    const defaultStr = defaultConfigString();

    if (config === defaultStr) {
      delete query[options.id];
    } else {
      query[options.id] = config;
    }

    if (hash(query) === hash(router.currentRoute.query)) return;

    if (persistMode === "url") {
      router.push({ query }).catch(() => {});
    } else {
      router.replace({ query }).catch(() => {});
    }
  }

  function restoreFromUrl(): boolean {
    if (persistMode === false) return false;

    let raw: string | null = null;

    if (persistMode === "localStorage") {
      raw = localStorage.getItem(`config/${options.id}`);
      if (raw && !options.saveWithPage) {
        const urlPage = (router.currentRoute.query["p"] as string) || "1";
        raw = `${urlPage}${SEP}${raw}`;
      }
    } else {
      raw = (router.currentRoute.query[options.id] as string) || null;
    }

    if (!raw) return false;

    deserialiseConfig(raw);
    lastSyncedConfig = serialiseConfig();
    return true;
  }

  function startRouteWatcher() {
    if (persistMode === false || persistMode === "localStorage") return;

    // Scoped to this instance's setup — auto-disposed on unmount via Vue's
    // effect scope. Only fires when our own query key changes, so unrelated
    // navigations (modal opens, preview routes) don't kick off a fetch.
    routeWatcherStop = watch(
      () => (vm?.$route.query?.[options.id] ?? null) as string | null,
      raw => {
        const defaultStr = defaultConfigString();

        // Skip if caused by our own sync
        if (raw === lastSyncedConfig) return;
        if (!raw && lastSyncedConfig === defaultStr) return;

        // External change detected (e.g. browser back/forward)
        if (raw) {
          deserialiseConfig(raw);
          lastSyncedConfig = raw;
        } else {
          page.value = 1;
          limit.value = defaultLimit;
          activeSort.value = defaultSort;
          activeFilters.value = [];
          lastSyncedConfig = defaultStr;
        }

        if (initialised) {
          cancel();
          // Defer to nextTick so reactive prop updates from the route change
          // (e.g. `manualFilters` flowing into `scopingParams`) flush before
          // we build request params — otherwise we fetch with stale scope
          // and the store's hash dedupe silently swallows the call.
          nextTick(() => {
            if (destroyed) return;
            executeFetch();
          });
        }
      }
    );
  }

  // ── Option Resolution Cache ──────────────────────────────────────────────

  const optionsCache = ref<Record<string, FilterOption[]>>({});
  const inflightOptions = new Map<string, Promise<FilterOption[]>>();

  async function resolveOptions(defId: string): Promise<FilterOption[]> {
    const existing = optionsCache.value[defId];
    if (existing) return existing;

    const inflight = inflightOptions.get(defId);
    if (inflight) return inflight;

    const def = filterDefs.value.find(d => d.id === defId);
    if (!def) return [];

    if (def.staticOptions) {
      optionsCache.value = {
        ...optionsCache.value,
        [defId]: def.staticOptions
      };
      return def.staticOptions;
    }

    const resolver = def.options;
    if (!resolver) return [];

    const promise = (async () => {
      try {
        const opts = (await resolver()) || [];
        optionsCache.value = { ...optionsCache.value, [defId]: opts };
        return opts;
      } finally {
        inflightOptions.delete(defId);
      }
    })();
    inflightOptions.set(defId, promise);
    return promise;
  }

  function getResolvedOptions(defId: string): FilterOption[] | undefined {
    return optionsCache.value[defId];
  }

  // Auto-resolve options for active filters so pill labels populate without
  // the user opening the modal. Triggered on URL restore (where activeFilters
  // gets replaced wholesale) and whenever filterDefs change (e.g. lazy
  // mixin-derived defs landing after a fetch resolves).
  watch(
    [activeFilters, filterDefs],
    () => {
      for (const af of activeFilters.value) {
        const def =
          filterDefs.value.find(d => d.id === af.defId) ||
          filterDefs.value.find(d => d.field === af.field);
        if (!def) continue;
        if (def.staticOptions) continue;
        if (typeof def.options !== "function") continue;
        if (optionsCache.value[def.id]) continue;
        if (inflightOptions.has(def.id)) continue;
        void resolveOptions(def.id);
      }
    },
    { immediate: true }
  );

  // ── Filter-picker hooks ───────────────────────────────────────────────────
  // Called by ListingControls when the "Add filter" dropdown opens. Providers
  // can register handlers to lazily fetch data needed only for the picker
  // (e.g. custom-field defs that themselves come from a network call).
  const filterPickerOpenHandlers: Array<() => void> = [];
  function onFilterPickerOpen(handler: () => void): () => void {
    filterPickerOpenHandlers.push(handler);
    return () => {
      const i = filterPickerOpenHandlers.indexOf(handler);
      if (i >= 0) filterPickerOpenHandlers.splice(i, 1);
    };
  }
  function notifyFilterPickerOpen(): void {
    for (const h of filterPickerOpenHandlers) {
      try {
        h();
      } catch {
        /* swallow — handler errors must not break the picker */
      }
    }
  }

  // ── Cleanup ─────────────────────────────────────────────────────────────

  onBeforeUnmount(() => {
    destroyed = true;
    cancel();
    routeWatcherStop?.();
    if (searchDebounceTimer) clearTimeout(searchDebounceTimer);
    if (process.env.NODE_ENV !== "production") {
      const count = (registeredIds.get(options.id) ?? 1) - 1;
      if (count <= 0) registeredIds.delete(options.id);
      else registeredIds.set(options.id, count);
    }
    store.dispatch("data/binList", {
      storeModule: options.module,
      scope,
      vm
    });
  });

  // ── Initialise ──────────────────────────────────────────────────────────

  restoreFromUrl();
  startRouteWatcher();
  initialised = true;

  // Refetch when scopingParams change (e.g. route prop change feeding
  // `manualFilters`). flush: "post" runs after Vue's render cycle, so the
  // reactive chain (props → toRef → manualFiltersRef → scopingParams) has
  // settled by the time we read params for the request.
  let lastScopingHash = hash(scopingParams.value);
  watch(
    scopingParams,
    next => {
      const nextHash = hash(next);
      if (nextHash === lastScopingHash) return;
      lastScopingHash = nextHash;
      cancel();
      void executeFetch();
    },
    { deep: true, flush: "post" }
  );

  // ── Return ──────────────────────────────────────────────────────────────

  return {
    data,
    total,
    isLoading,
    isFetching,
    hasError,
    search,
    filterDefs,
    activeFilters,
    lockedFilters,
    setFilters,
    addFilter,
    updateFilter,
    removeFilter,
    clearFilters,
    sorterDefs,
    activeSort,
    setSort,
    page,
    limit,
    setPage,
    setLimit,
    fetch,
    reload,
    cancel,
    resolveOptions,
    getResolvedOptions,
    onFilterPickerOpen,
    notifyFilterPickerOpen
  };
}
